import { useEffect, useState, useCallback } from 'react';
import { getEcho, reverbEnabled } from '@/utils/echo';
import { getSession } from '@/domains/auth/services/auth.service';
import { fetchUserNotifications } from '@/domains/api/notifications.api';
import type { ApiUserNotification } from '@/domains/api/types';

export function useUserNotifications(pollIntervalMs = 30000, enabled = true) {
  const [notifications, setNotifications] = useState<ApiUserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(reverbEnabled());

  const load = useCallback(async () => {
    try {
      const data = await fetchUserNotifications();
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    load();
    const session = getSession();
    const userId = session?.user?.id;
    const echo = getEcho(session?.token ?? null);

    if (echo && userId) {
      const channel = echo.private(`user.${userId}`);
      channel.listen('.notification.created', (payload: { notification: ApiUserNotification }) => {
        setNotifications((prev) => [payload.notification, ...prev]);
        setUnreadCount((c) => c + 1);
      });
      setLive(true);

      return () => {
        channel.stopListening('.notification.created');
        echo.leave(`user.${userId}`);
      };
    }

    const interval = setInterval(load, pollIntervalMs);
    setLive(false);
    return () => clearInterval(interval);
  }, [load, pollIntervalMs, enabled]);

  return { notifications, unreadCount, loading, live, reload: load };
}
